// What the PDF renderer is handed for a document (#621): the whole content
// of the page, already decided, so the service draws and decides nothing.
//
// Pure, and in `src/domain/` so `functions/domain/` mirrors it: the
// `documentLink` callable (`functions/documents/link.js`) builds these bodies
// from the stored source and hashes them, and a body that hashes the same as
// the one the stored PDF was drawn from is a PDF that need not be drawn
// again. So **everything the page shows has to be in the body** — the
// studio's name, logo and accent colour, its locale, the family's name, each
// line's student and room — or a change to it would not reach the PDF.
//
// ── The invoice ────────────────────────────────────────────────────────────
//
// The web's `InvoiceDocument.jsx`, as data. Every decision on it is the
// core's, through the same three adapters the drawer calls:
// `invoiceDocumentView` (the heading, the footer, the bottom line),
// `invoiceLineView` (which column a line's amount sits in under the
// invoice's own `columnRule`, #643, and a condensed line's `N × `),
// `invoiceLineDescription` (the line's words with the student it is for,
// #633) and `invoiceStudentGroups` (one heading per student). Dates
// are written here in the studio's short date, as `useDateFormat` writes
// them on screen; money goes across as minor units and the service writes it
// with the vendored core in the studio's locale and currency, as
// `useMoneyFormat` does.
//
// `lineDetail(line)` is `{ studentName, location }` — on screen
// `useInvoiceLineDetail`, on the server the same chain (ledger entry →
// occurrence → series → location) read with the admin SDK. A line whose
// lookup misses renders with no student and no room, as the screen's does:
// nothing is claimed that cannot be found.
//
// ── The report ─────────────────────────────────────────────────────────────
//
// `buildRenderRequest`, which the server builds from the stored
// `reportRequests` document. The rows are the ones the client folded and
// stored, unchanged: the folds stay the client's.

import {
  invoiceDocumentGroups, invoiceDocumentView, invoiceLineDescription, invoiceLineView, invoiceNegatives,
  invoiceStudentGroups,
} from './invoice.js';
import { DEFAULT_INVOICING } from './studio.js';
import { currencyFor, formatDateOnly, shortDatePattern } from './locale.js';
import { BRAND } from './brand.js';
import { columnTypesFor } from './reportRunner.js';
import { describeParams } from './reports.js';
import { documentFilename } from './documentPdf.js';

const NO_DETAIL = Object.freeze({ studentName: null, location: null });

const minor = (v) => Math.round(Number(v) || 0);

// The studio block both documents carry: its name, and how money is written.
function studioBlock({ studioName, locale, currency, accentColor, logo }) {
  return {
    name: studioName || '',
    currency: currency || currencyFor(locale),
    ...(locale ? { locale: String(locale) } : {}),
    ...(accentColor ? { accentColor: String(accentColor) } : {}),
    ...(logo ? { logo: String(logo) } : {}),
  };
}

/**
 * The body `POST /v1/documents:render` draws an invoice from.
 *
 * `invoice` is the stored snapshot (`invoicesV2`); `invoicing` the studio's
 * normalised Accounts & Invoices group; `studioName` the studio's display
 * name; `familyName` the family's; `lineDetail` the per-line lookup above.
 *
 * `issuing`: the body is the one being frozen onto an invoice created now
 * (`functions/invoicing/freeze.js`). Such a body is drawn as MMS draws an
 * invoice, and says so on itself:
 *
 *   - `negatives: 'parentheses'` (#649) — every negative figure on it is
 *     written `($10.00)` rather than `-$10.00`, by whoever draws it;
 *   - grouped by student, the lines that name no student come first under no
 *     heading, and each student's group carries `subtotal: { label, minor }`
 *     (#650), drawn as a last row `Nguyen, Ava Subtotal` with its figure in
 *     Charges. `invoiceDocumentGroups` states MMS's behaviour.
 *
 * Every other body — the live drawing of an invoice with no `frozen` (#622),
 * on screen and in its PDF — has no `negatives`, so a minus sign, and the
 * groups of `invoiceStudentGroups` with the unnamed lines last and no
 * subtotal. A frozen body is never rebuilt, so one frozen without these keeps
 * its text (#649, #650).
 */
export function invoiceRenderRequest(invoice, {
  studioName = '', familyName = '', invoicing = null, locale = null, currency = null,
  logo = null, lineDetail = null, issuing = false,
} = {}) {
  const inv = invoicing || DEFAULT_INVOICING;
  const detailFor = (line) => (lineDetail ? lineDetail(line) || NO_DETAIL : NO_DETAIL);
  const dateText = (iso) => (iso ? formatDateOnly(String(iso), locale) || String(iso) : '');
  const { heading, footer, dueLabel, dueMinor } = invoiceDocumentView(invoice, { studioName, invoicing: inv });
  const billTo = familyName || 'Family';

  const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
  const details = lines.map(detailFor);
  const views = lines.map((l) => invoiceLineView(l, invoice.columnRule));

  const lineRow = (l, i) => {
    const view = views[i];
    const location = inv.showLocation ? (details[i].location || null) : null;
    return {
      date: dateText(l.date),
      // The student the line is for, named as MMS names them (#633) —
      // whatever the grouping, and for a family of one as well.
      description: `${view.prefix}${invoiceLineDescription(l, details[i].studentName)}`,
      location,
      chargeMinor: view.chargeMinor,
      paymentMinor: view.paymentMinor,
    };
  };

  // One heading per student when the studio groups and the family has more
  // than one (`invoiceStudentGroups` says when); otherwise one group with no
  // heading, which the service draws as a plain table.
  const names = details.map((d) => d.studentName || '');
  let groups = [{ heading: null, lines: lines.map(lineRow) }];
  if (inv.groupChargesByStudent && issuing) {
    // MMS's groups (#650): each student's ends in its subtotal row.
    const grouped = invoiceDocumentGroups(names, views);
    if (grouped) {
      groups = grouped.map((g) => ({
        heading: g.heading,
        lines: g.lines.map((i) => lineRow(lines[i], i)),
        ...(g.subtotal ? { subtotal: g.subtotal } : {}),
      }));
    }
  } else if (inv.groupChargesByStudent) {
    const grouped = invoiceStudentGroups(names, familyName);
    if (grouped) groups = grouped.map((g) => ({ heading: g.heading, lines: g.lines.map((i) => lineRow(lines[i], i)) }));
  }

  const number = Number.isFinite(invoice.invoiceNumber) ? invoice.invoiceNumber : null;

  return {
    kind: 'invoice',
    product: BRAND.name,
    filename: documentFilename({ kind: 'invoice', number, party: familyName || null }),
    studio: studioBlock({
      studioName, locale, currency, accentColor: inv.accentColor, logo,
    }),
    invoice: {
      number,
      date: dateText(invoice.invoiceDate),
      from: heading,
      billTo,
      previousBalanceMinor: minor(invoice.previousBalanceMinor),
      paymentsMinor: minor(invoice.paymentsMinor),
      newChargesMinor: minor(invoice.newChargesMinor),
      dueLabel,
      dueMinor,
      groups,
      footer: footer || null,
      ...(issuing ? { negatives: invoiceNegatives() } : {}),
    },
  };
}

// ── An issued invoice is a fixed document (#622) ───────────────────────────
//
// An invoice's PDF is the one drawn when it was created, not whatever its
// studio's branding says when somebody next opens or emails it. So
// `generateInvoice` (functions/invoicing/) writes the whole body above onto
// the invoice as `frozen` when it creates it, the server renders the PDF from
// that and never again, and every screen draws a frozen invoice from it.
//
//   frozen: {
//     version: 1,
//     logo:    the logo's fingerprint (core/src/logo.rs), or null — its bytes
//              are kept once per logo at `invoiceLogos/{fingerprint}`, so an
//              invoice does not carry a quarter of a megabyte into every
//              screen that lists invoices;
//     body:    `invoiceRenderRequest`'s answer with `studio.logo` taken off:
//              the core's decisions (heading, footer, due line, the lines'
//              columns and student groups), the accent colour, the locale and
//              currency, the family's name and each line's date, student and
//              room, all as they were.
//   }
//
// An invoice with no `frozen` predates #622 and is drawn live, on screen and
// in its PDF, under the current grouping, location and student-name rules.
//
// A frozen body is never rebuilt. These were added without changing the shape
// or `version`, since an older reader can pass over them:
//
//   - each line's student in its description (#633), e.g.
//     `Nguyen, Ben - Sibling discount`. A body frozen between #622 and #633
//     carries the entry's words with no student, and is drawn so.
//   - `invoice.negatives: 'parentheses'` and, grouped by student, a `subtotal`
//     on each student's group (#649, #650). A body without them is drawn with
//     a minus sign and no subtotal rows, as is any body read by a phone or
//     browser that predates them.

/** The version of `frozen` this code writes and reads. */
export const FROZEN_INVOICE_VERSION = 1;

/** What an invoice keeps of `body`, its logo kept by `logoFingerprint`. */
export function frozenInvoice(body, logoFingerprint = null) {
  // The bytes go; the rest of the studio block stays.
  const studio = { ...(body?.studio || {}) };
  delete studio.logo;
  return { version: FROZEN_INVOICE_VERSION, logo: logoFingerprint || null, body: { ...body, studio } };
}

/** The invoice's frozen body, or null for one drawn live. */
export function frozenBodyOf(invoice) {
  const f = invoice?.frozen;
  return f && f.version === FROZEN_INVOICE_VERSION && f.body?.kind === 'invoice' && f.body.invoice ? f.body : null;
}

/** A frozen body with its logo's data URI put back, for the renderer. */
export function frozenRenderBody(body, logo = null) {
  return logo ? { ...body, studio: { ...body.studio, logo: String(logo) } } : body;
}

/**
 * A stored report request → the body the service renders.
 *
 * The rows and columns go across exactly as `runReport` produced them on the
 * client that ran the report. `studioName` is the studio's own name;
 * `currency` follows the studio's locale unless a caller names it (MMS has
 * no currency setting — the Region decides it, `src/domain/locale.js`), and
 * a wrong one is a hundredfold error on a filed document. `locale` is how
 * money is *written*, as `format()` writes it on screen (#463). `accentColor`
 * and `logo` are the invoice's branding, which MMS's report PDF reuses (#448,
 * #498). `datePattern` writes the parameters restated at the top, as the
 * result drawer writes them.
 *
 * `moneyEncoding` is stated outright, never derived: runReport writes money
 * as a decimal string in the studio's currency's places (`12.34`, `1234` for
 * yen), and the service refuses to guess between that and minor units.
 */
export function buildRenderRequest(report, {
  studioName, locale, currency, generatedOn, accentColor, logo, datePattern,
} = {}) {
  return {
    typeId: report.typeId,
    title: report.label || report.typeId,
    studio: studioBlock({ studioName, locale, currency, accentColor, logo }),
    // The product's name, for the PDF's Creator field. Sent rather than known
    // by the service: it is deployed from its own directory and cannot read
    // design/brand.json, and a name typed into it would be a second copy.
    product: BRAND.name,
    params: describeParams(report.typeId, report.params || {}, datePattern || shortDatePattern(locale)),
    columns: report.result?.columns || [],
    columnTypes: columnTypesFor(report.typeId),
    moneyEncoding: 'decimal',
    rows: report.result?.rows || [],
    // The business date the request was made on, not today: a report reprinted
    // in November is still the one that was run in August.
    generatedOn: generatedOn || report.createdOn,
    supported: report.status !== 'unsupported',
    reason: report.reason || null,
  };
}

/**
 * The body `POST /v1/documents:render` draws a report from: the report
 * request above, wrapped with its kind and its file's name.
 */
export function reportRenderRequest(report, options = {}) {
  const body = buildRenderRequest(report, options);
  return {
    kind: 'report',
    product: body.product,
    filename: documentFilename({ kind: 'report', title: report.label || null, date: body.generatedOn || '' }),
    report: body,
  };
}

/**
 * The canonical text of a body: keys sorted at every depth, so two bodies
 * that say the same thing are the same string whatever order their fields
 * were written in. What the server hashes to decide whether a stored PDF is
 * still the one this body draws (`functions/documents/request.js`).
 */
export function canonicalJson(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map((v) => (v === undefined ? 'null' : canonicalJson(v))).join(',')}]`;
  const keys = Object.keys(value).filter((k) => value[k] !== undefined).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(value[k])}`).join(',')}}`;
}
